import React from 'react';
import Icon from '../../../components/AppIcon';

const AICapabilityScoring = ({ score, breakdown, recommendations }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-error';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center gap-12">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground font-heading">
              AI Capability Scoring
            </h2>
            <p className="text-sm text-muted-foreground caption mt-2">
              Comprehensive capability assessment powered by AI analysis
            </p>
          </div>
        </div>
      </div>
      <div className="p-16 md:p-20 lg:p-24 space-y-20 md:space-y-24">
        <div className="flex flex-col lg:flex-row items-center gap-20 md:gap-24">
          <div className="relative w-48 h-48 md:w-56 md:h-56 flex-shrink-0">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="50%"
                cy="50%"
                r="40%"
                fill="none"
                stroke="currentColor"
                strokeWidth="12"
                className="text-muted"
              />
              <circle
                cx="50%"
                cy="50%"
                r="40%"
                fill="none"
                stroke="currentColor"
                strokeWidth="12"
                strokeDasharray={`${2 * Math.PI * 0.4 * (score / 100) * 100} ${2 * Math.PI * 0.4 * 100}`}
                className={getScoreColor(score)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`text-4xl md:text-5xl font-bold ${getScoreColor(score)} data-text`}>
                {score}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground caption mt-2">
                {getScoreLabel(score)}
              </div>
            </div>
          </div>

          <div className="flex-1 w-full space-y-16">
            <h3 className="text-sm font-semibold text-foreground mb-12">Capability Breakdown</h3>
            {breakdown?.map((item, index) => (
              <div key={index} className="space-y-8">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">{item?.category}</span>
                  <span className={`text-sm font-semibold data-text ${getScoreColor(item?.score)}`}>
                    {item?.score}/100
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getScoreBg(item?.score)} transition-smooth`}
                    style={{ width: `${item?.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-16 md:p-20">
          <h3 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
            <Icon name="Lightbulb" size={18} className="text-accent" />
            AI-Powered Recommendations
          </h3>
          <div className="space-y-12">
            {recommendations?.map((rec, index) => (
              <div key={index} className="flex items-start gap-12">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-2 ${rec?.priority === 'high' ? 'bg-error/10' : rec?.priority === 'medium' ? 'bg-warning/10' : 'bg-primary/10'}`}>
                  <Icon
                    name={rec?.priority === 'high' ? 'AlertCircle' : rec?.priority === 'medium' ? 'AlertTriangle' : 'Info'}
                    size={14}
                    className={rec?.priority === 'high' ? 'text-error' : rec?.priority === 'medium' ? 'text-warning' : 'text-primary'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground mb-4">{rec?.recommendation}</p>
                  <div className="flex flex-wrap items-center gap-8 text-xs caption">
                    <span className={`px-6 py-2 rounded-md ${rec?.priority === 'high' ? 'bg-error/10 text-error' : rec?.priority === 'medium' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}`}>
                      {rec?.priority?.charAt(0)?.toUpperCase() + rec?.priority?.slice(1)} Priority
                    </span>
                    <span className="text-muted-foreground">
                      Impact: +{rec?.impact} points
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 md:gap-16">
          <div className="bg-success/10 rounded-lg p-16 border border-success/20">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="TrendingUp" size={18} className="text-success" />
              <span className="text-xs font-medium text-success caption">Strengths</span>
            </div>
            <div className="text-xl md:text-2xl font-semibold text-success data-text">
              {breakdown?.filter(b => b?.score >= 80)?.length}
            </div>
            <div className="text-xs text-muted-foreground caption mt-4">
              Areas of excellence
            </div>
          </div>

          <div className="bg-warning/10 rounded-lg p-16 border border-warning/20">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="Target" size={18} className="text-warning" />
              <span className="text-xs font-medium text-warning caption">Opportunities</span>
            </div>
            <div className="text-xl md:text-2xl font-semibold text-warning data-text">
              {breakdown?.filter(b => b?.score >= 60 && b?.score < 80)?.length}
            </div>
            <div className="text-xs text-muted-foreground caption mt-4">
              Areas for growth
            </div>
          </div>

          <div className="bg-error/10 rounded-lg p-16 border border-error/20">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="AlertCircle" size={18} className="text-error" />
              <span className="text-xs font-medium text-error caption">Critical Gaps</span>
            </div>
            <div className="text-xl md:text-2xl font-semibold text-error data-text">
              {breakdown?.filter(b => b?.score < 60)?.length}
            </div>
            <div className="text-xs text-muted-foreground caption mt-4">
              Requires immediate attention
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICapabilityScoring;